import React from "react";
import {
    // Accordion,
    // AccordionSummary,
    AccordionDetails,
    Grid,
    Typography,
    MenuItem,
    Box,
    Autocomplete,
} from "@mui/material";
// import { ExpandMoreOutlined } from "@mui/icons-material";
import { StyledTextField, StyledTextareaAutosize } from "../../utilities/styles/checkoutPage.style";

interface AdditionalDetailsAccordionProps {
    customNames: string[];
    overrides: any[];
    selectedNames: string[];
    detailsExpended: boolean;
    orderPickListOptions: any[];
    organizationSettings: any;
    orderType?: string;
    classes: any;
    isRequiredKey: string;
    eventNotes?: string;
    validator?: any;
    onChange: (e: any, fieldName: string) => void;
    onToggle: () => void;
    onStateChange: (updates: any) => void;
    notesOnly?: boolean;
}

const AdditionalDetailsAccordion: React.FC<AdditionalDetailsAccordionProps> = ({
    customNames,
    overrides,
    selectedNames,
    detailsExpended,
    orderPickListOptions,
    organizationSettings,
    classes,
    isRequiredKey,
    eventNotes,
    validator,
    onChange,
    onToggle,
    onStateChange,
    orderType,
    notesOnly = false,
}) => {
    const isSelectedKey = isRequiredKey.replace("Required", "Selected");

    const handleFieldChange = (newValue: any, name: string, index: number, pickListId: string) => {
        const newSelectedOptions = [...selectedNames];
        const selectedOptionId = newValue ? newValue.id : "";
        newSelectedOptions[index] = selectedOptionId;

        const currentOrderPickListOptions = orderPickListOptions || [];
        const existingIndex = currentOrderPickListOptions.findIndex(
            (item: any) => item.pickListId === pickListId
        );

        let updatedOptions;
        if (selectedOptionId && pickListId) {
            if (existingIndex >= 0) {
                updatedOptions = [...currentOrderPickListOptions];
                updatedOptions[existingIndex] = {
                    pickListId: pickListId,
                    pickListOptionId: selectedOptionId,
                };
            } else {
                updatedOptions = [
                    ...currentOrderPickListOptions,
                    { pickListId: pickListId, pickListOptionId: selectedOptionId },
                ];
            }
        } else {
            updatedOptions = currentOrderPickListOptions.filter(
                (item: any) => item.pickListId !== pickListId
            );
        }

        onStateChange({
            selectedNames: newSelectedOptions,
            orderPickListOptions: updatedOptions,
            requiredFieldsError: false,
        });

        onChange({ target: { value: selectedOptionId } }, name);
    };

    const handleCustomValueChange = (e: any, name: string, index: number, pickListId: string) => {
        const value = e.target.value;
        const newSelectedOptions = [...selectedNames];
        newSelectedOptions[index] = value;

        const currentOrderPickListOptions = orderPickListOptions || [];

        const existingIndex = currentOrderPickListOptions.findIndex(
            (item: any) => item.pickListId === pickListId
        );

        let updatedOptions;

        if (existingIndex >= 0) {
            updatedOptions = [...currentOrderPickListOptions];
            updatedOptions[existingIndex] = {
                ...updatedOptions[existingIndex],
                pickListOptionCustomValue: value,
            };
        } else {
            updatedOptions = [
                ...currentOrderPickListOptions,
                {
                    pickListId,
                    pickListOptionCustomValue: value,
                },
            ];
        }

        onStateChange({
            selectedNames: newSelectedOptions,
            orderPickListOptions: updatedOptions,
            requiredFieldsError: false,
        });

        onChange(e, name);
    };

    const isExpanded = overrides.some((item: any) => item[isRequiredKey])
        ? !detailsExpended
        : detailsExpended;

    const additionalDetails = (
        <AccordionDetails
            className={classes.expansionPanelContent}
            sx={{
                width: { xs: "calc(100vw - 45px)", sm: "100%" },
                padding: 0,
            }}
        >
            <Grid container spacing={2} direction="row">
                {!notesOnly && customNames.map((name: string, index: number) => {
                    const matchingOverride = overrides.find((event: any) => {
                        if (name === "") {
                            return (
                                event.customName === "" && overrides.indexOf(event) === index
                            );
                        }
                        return event.customName === name;
                    });

                    const isSelected = matchingOverride?.[isSelectedKey] !== false;
                    if (!isSelected) {
                        return null;
                    }

                    const pickListId = matchingOverride?.pickListId;
                    const options = matchingOverride?.pickList?.pickListOptions || [];
                    const hasOptions = options.length > 0;
                    const displayName =
                        name === "" ? matchingOverride?.pickList?.name : name;
                    const isRequired = matchingOverride?.[isRequiredKey] === true;
                    const hasError =
                        isRequired && (!selectedNames[index] || selectedNames[index] === "");

                    // No options configured for this picklist: show free-text field
                    if (!hasOptions) {
                        return (
                            <Grid item xs={12} sm={6} key={index}>
                                <StyledTextField
                                    name={name}
                                    id={`theme-${index}`}
                                    label={displayName}
                                    value={selectedNames[index] || ""}
                                    onChange={(e: any) =>
                                        handleCustomValueChange(e, name, index, pickListId)
                                    }
                                    fullWidth
                                    variant="outlined"
                                    required={isRequired}
                                    error={hasError}
                                    helperText={hasError ? "Additional Details are required" : ""}
                                />
                            </Grid>
                        );
                    }

                    const selectedOption =
                        options.find((option: any) => option.id === selectedNames[index]) || null;

                    return (
                        <Grid item xs={12} sm={6} key={index}>
                            <Autocomplete
                                options={options}
                                getOptionLabel={(option: any) => option?.name || ""}
                                isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                                value={selectedOption}
                                onChange={(_e: any, newValue: any) =>
                                    handleFieldChange(newValue, name, index, pickListId)
                                }
                                noOptionsText="No records found"
                                renderOption={(props: any, option: any) => (
                                    <MenuItem
                                        {...props}
                                        key={`menu-${option.id}`}
                                        sx={{ fontSize: { xs: "18px" } }}
                                    >
                                        {option.name}
                                    </MenuItem>
                                )}
                                renderInput={(params: any) => (
                                    <StyledTextField
                                        {...params}
                                        name={name}
                                        id={`theme-${index}`}
                                        label={displayName}
                                        variant="outlined"
                                        required={isRequired}
                                        error={hasError}
                                        helperText={hasError ? "Additional Details are required" : ""}
                                    />
                                )}
                            />
                        </Grid>
                    );
                })}
                {eventNotes !== undefined && validator && (
                    <Grid item xs={12} sm={12} mt={2}>
                        <StyledTextareaAutosize
                            name="eventNotes"
                            autoComplete="off"
                            className={classes.textareaBoxSize}
                            id="eventNotes"
                            onChange={(e) => onChange(e, "eventNotes")}
                            onBlur={() =>
                                validator.message(
                                    "event notes",
                                    typeof eventNotes === "string" ? eventNotes.trim() : eventNotes,
                                    "noSpecialChar"
                                )
                            }
                            placeholder="Notes"
                            minRows={5}
                            maxRows={5}
                            value={eventNotes}
                        />
                        {validator.message("eventNotes", eventNotes, "noSpecialChar")}
                    </Grid>
                )}
            </Grid>
        </AccordionDetails>
    );

    return (
        <Box>
           {orderType === "Event" && <Grid item xs={12} sm={12}>
                <Typography variant="caption" component="div" gutterBottom mt={2}>
                    {organizationSettings?.ordersTermsMessage}
                </Typography>
            </Grid>}
            {additionalDetails}
        </Box>
    );
};

export default AdditionalDetailsAccordion;
